// Rules panel: fetches the per-language rule inventory (generated by
// `lt-cli inventory --json`) and turns checkboxes into engine options.

const MAX_VISIBLE = 500;

export function createRulesPanel({
  dialog,
  languageLabel,
  pickyInput,
  searchInput,
  list,
  summary,
  applyButton,
  resetButton,
  closeButton,
  onApply,
}) {
  let generation = 0;
  let pack = null;
  let groups = new Map();
  let dirty = false;
  const inventoryCache = new Map();

  async function ensureInventory(packName) {
    if (inventoryCache.has(packName)) {
      return inventoryCache.get(packName);
    }
    const url = new URL(
      `${import.meta.env.BASE_URL}rules/${packName}.json`,
      document.baseURI,
    );
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`cannot load ${url.pathname}: HTTP ${response.status}`);
    }
    const grouped = groupRules(await response.json());
    inventoryCache.set(packName, grouped);
    return grouped;
  }

  function groupRules(entries) {
    const grouped = new Map();
    for (const rule of entries) {
      let group = grouped.get(rule.id);
      if (!group) {
        group = {
          id: rule.id,
          name: rule.name || rule.id,
          category: rule.categoryName || rule.categoryId || "",
          defaultOn: false,
          picky: true,
          complex: false,
          checked: false,
        };
        grouped.set(rule.id, group);
      }
      group.defaultOn ||= Boolean(rule.defaultOn);
      group.picky &&= Boolean(rule.picky);
      group.complex ||= Boolean(rule.complex);
      if (rule.name && group.name === group.id) {
        group.name = rule.name;
      }
    }
    return grouped;
  }

  function effectiveChecked(group) {
    return group.defaultOn && (!group.picky || pickyInput.checked);
  }

  function resetState() {
    for (const group of groups.values()) {
      group.checked = effectiveChecked(group);
    }
    dirty = false;
    render();
  }

  function updateSummary() {
    if (groups.size === 0) {
      summary.textContent = "";
      applyButton.disabled = true;
      return;
    }
    let enabled = 0;
    for (const group of groups.values()) {
      if (group.checked) {
        enabled += 1;
      }
    }
    summary.textContent = `${enabled} of ${groups.size} rules enabled${
      dirty ? " · changes not applied" : ""
    }`;
    applyButton.disabled = !dirty;
  }

  function render() {
    const query = searchInput.value.trim().toLowerCase();
    const rows = [...groups.values()]
      .filter((group) => {
        if (!query) {
          return true;
        }
        return (
          group.id.toLowerCase().includes(query) ||
          group.name.toLowerCase().includes(query) ||
          group.category.toLowerCase().includes(query)
        );
      })
      .sort((a, b) => a.name.localeCompare(b.name));

    list.replaceChildren();
    for (const group of rows.slice(0, MAX_VISIBLE)) {
      const row = document.createElement("label");
      row.className = "rule-row";
      row.dataset.picky = group.picky ? "true" : "false";

      const checkbox = document.createElement("input");
      checkbox.type = "checkbox";
      checkbox.checked = group.checked;
      checkbox.disabled = group.picky || group.complex;
      checkbox.addEventListener("change", () => {
        group.checked = checkbox.checked;
        dirty = true;
        updateSummary();
      });
      row.append(checkbox);

      const main = document.createElement("span");
      main.className = "rule-main";
      const name = document.createElement("span");
      name.className = "rule-name";
      name.textContent = group.name;
      const meta = document.createElement("span");
      meta.className = "rule-meta";
      const badges = [];
      if (group.category) {
        badges.push(group.category);
      }
      if (!group.defaultOn) {
        badges.push("default off");
      }
      if (group.picky) {
        badges.push("picky");
      }
      if (group.complex) {
        badges.push("unsupported");
      }
      meta.textContent = [group.id, ...badges].join(" · ");
      main.append(name, meta);
      row.append(main);
      list.append(row);
    }
    if (rows.length > MAX_VISIBLE) {
      const note = document.createElement("p");
      note.className = "rules-note";
      note.textContent = `Showing ${MAX_VISIBLE} of ${rows.length} matching rules; refine the filter to see more.`;
      list.append(note);
    }
    updateSummary();
  }

  /** Open the panel for a pack; resets the selection when the pack changes. */
  async function show(packName, label) {
    const currentGeneration = ++generation;
    languageLabel.textContent = label ? `— ${label}` : "";
    if (!dialog.open) {
      dialog.showModal();
    }
    const changed = packName !== pack;
    if (changed) {
      pack = packName;
      list.replaceChildren(placeholder("loading rules…"));
    }
    let inventory;
    try {
      inventory = await ensureInventory(packName);
    } catch (error) {
      list.replaceChildren(placeholder(`could not load rules: ${error.message}`));
      return;
    }
    if (currentGeneration !== generation) {
      return;
    }
    groups = inventory;
    const hasPicky = [...groups.values()].some((group) => group.picky);
    pickyInput.disabled = !hasPicky;
    pickyInput.parentElement.title = hasPicky
      ? ""
      : "this language has no picky rules";
    if (changed) {
      searchInput.value = "";
      pickyInput.checked = false;
      resetState();
    } else {
      render();
    }
  }

  function placeholder(text) {
    const note = document.createElement("p");
    note.className = "rules-note";
    note.textContent = text;
    return note;
  }

  pickyInput.addEventListener("change", () => {
    for (const group of groups.values()) {
      if (group.picky) {
        group.checked = pickyInput.checked;
      }
    }
    dirty = true;
    render();
  });
  searchInput.addEventListener("input", render);
  resetButton.addEventListener("click", () => {
    pickyInput.checked = false;
    resetState();
  });
  closeButton.addEventListener("click", () => dialog.close());
  applyButton.addEventListener("click", () => {
    const enabledRules = [];
    const disabledRules = [];
    for (const group of groups.values()) {
      if (group.picky) {
        continue; // controlled by the picky switch
      }
      if (group.checked && !group.defaultOn) {
        enabledRules.push(group.id);
      }
      if (!group.checked && group.defaultOn) {
        disabledRules.push(group.id);
      }
    }
    onApply({ picky: pickyInput.checked, enabledRules, disabledRules });
    dirty = false;
    updateSummary();
  });

  return { show };
}
